/**
 * sindhi-collection.js
 * Sindhi Dry Fruits — Collection page behaviours
 * ─────────────────────────────────────────────────────────────────────────────
 * A. Sticky filter-chip bar morph (IntersectionObserver on sentinel)
 * B. Smart-banner active-tile tracking (sessionStorage)
 * C. ATC AJAX hook (Shopify Cart API, fires sindhi:cart-updated)
 * D. Variant pill handler (price update, savings badge)
 * E. Wishlist toggle (localStorage shim)
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /* ────────────────────────────────────────────────────────────────────────
     Config / constants
     ──────────────────────────────────────────────────────────────────────── */
  var CART_ADD_URL      = '/cart/add.js';
  var CART_EVENT        = 'sindhi:cart-updated';
  var ADDED_RESET_MS    = 1200;
  var ERROR_RESET_MS    = 2000;
  var WISHLIST_KEY      = 'sindhi-wishlist';
  var BANNER_TILE_KEY   = 'sindhi-last-banner-tile';

  /* ────────────────────────────────────────────────────────────────────────
     Helpers
     ──────────────────────────────────────────────────────────────────────── */
  function qs(sel, ctx)  { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /** Format Shopify money (cents integer) to ₹X,XXX using Intl */
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(cents / 100);
  }

  /** Read wishlist array from localStorage */
  function readWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  /** Write wishlist array to localStorage */
  function writeWishlist(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable — fail silently, no crash */
    }
  }

  /* ────────────────────────────────────────────────────────────────────────
     A. Sticky filter-chip bar morph
     ──────────────────────────────────────────────────────────────────────── */
  function initFilterBarMorph() {
    var bar      = qs('[data-scfc-bar]');
    var sentinel = qs('[data-scfc-sentinel]');
    if (!bar || !sentinel) return;

    var rootEl = document.documentElement;

    /* Merchant-configured sticky offset (desktop/tablet) assumes a
       persistently-pinned header sitting above the bar — captured once,
       before any JS override, so it can be restored if that assumption
       ever becomes true again. */
    var configuredOffset       = bar.style.getPropertyValue('--scfc-sticky-offset');
    var configuredOffsetTablet = bar.style.getPropertyValue('--scfc-sticky-offset-tablet');

    /** Is #sindhi-header actually pinned to the viewport right now?
     * On desktop/tablet (≥750px) it currently never is — its glass-pin
     * CSS (position:fixed on .mh--sticky) is scoped to
     * @media max-width:749px only, so #header-group scrolls away like
     * normal content above 749px (confirmed live 2026-07-12: #sh-nav-row
     * computes position:sticky but its containing block has zero extra
     * height to stick within). Ducking the offset to 0 there closes the
     * gap where scrolled-past content bled through above the fixed bar.
     * Checking live rather than hardcoding keeps this correct if the
     * header's own sticky behaviour is ever fixed on wider breakpoints. */
    function headerIsPinned() {
      var header = document.getElementById('sindhi-header');
      if (!header) return false;
      var cs = getComputedStyle(header);
      return (cs.position === 'fixed' || cs.position === 'sticky') &&
        header.getBoundingClientRect().top <= 2;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          /* Order is critical — all three happen synchronously so the
             browser paints them in a single frame with zero layout shift:
             1. Measure bar height while it is still in normal flow.
             2. Expand sentinel to that height (fills the gap the bar will
                vacate — section wrapper stays the same total height).
             3. Apply position:fixed (bar leaves flow; sentinel fills it). */
          var h = bar.offsetHeight;
          sentinel.style.height = h + 'px';
          bar.setAttribute('data-scfc-stuck', 'true');
          /* Once the filter bar owns the top, slide the mobile header out of
             the way so the two don't overlap (see the collision handled by
             CSS keyed on this attribute in sindhi-header +
             sindhi-collection-filter-chips). Mobile-only effect — the CSS is
             media-scoped, so setting the flag on all breakpoints is harmless. */
          rootEl.setAttribute('data-scfc-header-hidden', 'true');

          if (window.innerWidth >= 750) {
            if (headerIsPinned()) {
              bar.style.setProperty('--scfc-sticky-offset', configuredOffset);
              bar.style.setProperty('--scfc-sticky-offset-tablet', configuredOffsetTablet);
            } else {
              bar.style.setProperty('--scfc-sticky-offset', '0px');
              bar.style.setProperty('--scfc-sticky-offset-tablet', '0px');
            }
          }
        } else {
          /* Reverse: release fixed first, then collapse sentinel, then bring
             the header back. */
          bar.removeAttribute('data-scfc-stuck');
          sentinel.style.height = '';
          rootEl.removeAttribute('data-scfc-header-hidden');
        }
      });
    }, { rootMargin: '-1px 0px 0px 0px', threshold: 0 });

    observer.observe(sentinel);
  }

  /* ────────────────────────────────────────────────────────────────────────
     B. Smart-banner active-tile tracking
     ──────────────────────────────────────────────────────────────────────── */
  function initSmartBannerTracking() {
    var rail  = qs('[data-scsb-rail]');
    if (!rail) return;

    var tiles = qsa('[data-scsb-tile]', rail);
    if (!tiles.length) return;

    /* Rehydrate active state from sessionStorage */
    var lastId = '';
    try {
      lastId = sessionStorage.getItem(BANNER_TILE_KEY) || '';
    } catch (e) {}

    if (lastId) {
      tiles.forEach(function (tile) {
        if (tile.getAttribute('data-scsb-tile-id') === lastId) {
          tile.setAttribute('data-scsb-active', 'true');
        }
      });
    }

    /* Persist click + fire GA4 select_promotion event for measurement.
       GA4 expects `select_promotion` with promotion_id (tile id),
       promotion_name (headline text), creative_slot (rail position). */
    tiles.forEach(function (tile, idx) {
      tile.addEventListener('click', function () {
        var tileId = tile.getAttribute('data-scsb-tile-id');
        if (!tileId) return;
        try {
          sessionStorage.setItem(BANNER_TILE_KEY, tileId);
        } catch (e) {}

        /* GA4 select_promotion via dataLayer if present (gtag tag manager) */
        var headline = tile.querySelector('.scsb-tile__headline');
        var promoName = headline ? headline.textContent.trim() : tileId;
        if (window.dataLayer && Array.isArray(window.dataLayer)) {
          window.dataLayer.push({
            event: 'select_promotion',
            ecommerce: {
              creative_slot: 'smart_banner_' + (idx + 1),
              promotion_id: tileId,
              promotion_name: promoName,
              location_id: 'collection_page_top'
            }
          });
        }
        /* Also fire generic gtag if present */
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'select_promotion', {
            promotion_id: tileId,
            promotion_name: promoName,
            creative_slot: 'smart_banner_' + (idx + 1),
            location_id: 'collection_page_top'
          });
        }
      });
    });
  }

  /* ────────────────────────────────────────────────────────────────────────
     C. ATC AJAX hook
     ──────────────────────────────────────────────────────────────────────── */
  function initATC() {
    /* Delegate from document so it works after AJAX grid refreshes */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-spc-atc]');
      if (!btn) return;
      /* Guard: only inside a collection card */
      if (!btn.closest('[data-spc-card]') && !btn.closest('.spc-card')) return;

      e.preventDefault();

      /* Variant ID lives in the form's hidden input (canonical Shopify pattern).
         Fall back to button data-variant-id if the input is missing. */
      var card     = btn.closest('[data-spc-card]') || btn.closest('.spc-card');
      var atcInput = card ? qs('[data-spc-atc-input]', card) : null;
      var variantId = (atcInput && atcInput.value) || btn.getAttribute('data-variant-id') || '';
      if (!variantId) return;

      var defaultLabel = btn.getAttribute('data-label-add')   || '';
      var addedLabel   = btn.getAttribute('data-label-added') || 'Added!';
      var errorLabel   = btn.getAttribute('data-label-error') || 'Try again';

      /* Label lives inside a span so the spinner sibling survives state changes */
      var labelEl = qs('[data-spc-atc-label]', btn);
      if (labelEl && !defaultLabel) defaultLabel = labelEl.textContent.trim();

      function setLabel(text) {
        if (labelEl) labelEl.textContent = text;
        else         btn.textContent     = text;
      }

      /* Pending state */
      btn.setAttribute('data-spc-atc-state', 'pending');
      btn.disabled = true;

      fetch(CART_ADD_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart add failed: ' + res.status);
          return res.json();
        })
        .then(function (response) {
          /* Success */
          btn.setAttribute('data-spc-atc-state', 'added');
          setLabel(addedLabel);
          btn.disabled = false;

          document.dispatchEvent(new CustomEvent(CART_EVENT, {
            bubbles: true,
            detail:  { variantId: variantId, response: response }
          }));

          setTimeout(function () {
            btn.setAttribute('data-spc-atc-state', '');
            setLabel(defaultLabel);
          }, ADDED_RESET_MS);
        })
        .catch(function () {
          /* Error */
          btn.setAttribute('data-spc-atc-state', 'error');
          setLabel(errorLabel);
          btn.disabled = false;

          setTimeout(function () {
            btn.setAttribute('data-spc-atc-state', '');
            setLabel(defaultLabel);
          }, ERROR_RESET_MS);
        });
    });
  }

  /* ────────────────────────────────────────────────────────────────────────
     D. Variant pill handler
     ──────────────────────────────────────────────────────────────────────── */
  function initVariantPills() {
    /* Delegate — handles pills in AJAX-refreshed grids too */
    document.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-spc-pill]');
      if (!pill) return;

      var card = pill.closest('[data-spc-card]');
      if (!card) return;

      /* Skip pills that mark an unavailable variant */
      if (pill.getAttribute('data-variant-available') === 'false') return;

      /* Update active state on siblings — class + aria-pressed (matches snippet markup) */
      var siblings = qsa('[data-spc-pill]', card);
      siblings.forEach(function (p) {
        p.classList.remove('is-active');
        p.setAttribute('aria-pressed', 'false');
      });
      pill.classList.add('is-active');
      pill.setAttribute('aria-pressed', 'true');

      /* Read variant data */
      var variantId      = pill.getAttribute('data-variant-id')       || '';
      var priceRaw       = parseInt(pill.getAttribute('data-variant-price'),   10) || 0;
      var compareRaw     = parseInt(pill.getAttribute('data-variant-compare'), 10) || 0;
      var minDiscount    = parseInt(card.getAttribute('data-spc-min-discount'), 10) || 10;

      /* Update card's current variant */
      card.setAttribute('data-current-variant', variantId);

      /* Update price display — selectors match snippet's actual data attributes */
      var priceEl   = qs('[data-spc-price]', card);
      var compareEl = qs('[data-spc-price-compare]', card);
      var savingsEl = qs('[data-spc-price-save]', card);

      if (priceEl) priceEl.textContent = formatMoney(priceRaw);

      if (compareEl) {
        if (compareRaw > priceRaw) {
          compareEl.textContent = formatMoney(compareRaw);
          compareEl.hidden      = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden      = true;
        }
      }

      /* Savings badge: show only when discount >= threshold */
      if (savingsEl) {
        var discountPct = compareRaw > priceRaw
          ? Math.round(((compareRaw - priceRaw) / compareRaw) * 100)
          : 0;

        if (discountPct >= minDiscount) {
          var saveAmt      = formatMoney(compareRaw - priceRaw);
          var saveTemplate = savingsEl.getAttribute('data-save-template') || 'Save __AMOUNT__';
          savingsEl.textContent = saveTemplate.replace('__AMOUNT__', saveAmt);
          savingsEl.hidden      = false;
        } else {
          savingsEl.textContent = '';
          savingsEl.hidden      = true;
        }
      }

      /* Sync ATC: form hidden input + button data attribute */
      var atcInput = qs('[data-spc-atc-input]', card);
      if (atcInput) atcInput.value = variantId;

      var atcBtn = qs('[data-spc-atc]', card);
      if (atcBtn) atcBtn.setAttribute('data-variant-id', variantId);
    });
  }

  /* ────────────────────────────────────────────────────────────────────────
     E. Wishlist toggle (localStorage shim)
     ──────────────────────────────────────────────────────────────────────── */
  function initWishlist() {
    /* Rehydrate on page load */
    var list = readWishlist();
    qsa('[data-spc-wishlist]').forEach(function (btn) {
      var card      = btn.closest('[data-spc-card]');
      var productId = btn.getAttribute('data-product-id');
      if (!productId) return;

      var wishlisted = list.indexOf(productId) !== -1;
      btn.setAttribute('aria-pressed', String(wishlisted));
      if (card) card.setAttribute('data-spc-wishlisted', String(wishlisted));
    });

    /* Toggle on click — delegate to catch AJAX-injected cards */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-spc-wishlist]');
      if (!btn) return;

      var card      = btn.closest('[data-spc-card]');
      var productId = btn.getAttribute('data-product-id');
      if (!productId) return;

      var current = readWishlist();
      var idx     = current.indexOf(productId);
      var added;

      if (idx === -1) {
        current.push(productId);
        added = true;
      } else {
        current.splice(idx, 1);
        added = false;
      }

      writeWishlist(current);
      btn.setAttribute('aria-pressed', String(added));
      if (card) card.setAttribute('data-spc-wishlisted', String(added));
    });
  }

  /* ────────────────────────────────────────────────────────────────────────
     F. Load-more (AJAX append next page into existing grid)
     ──────────────────────────────────────────────────────────────────────── */
  function initLoadMore() {
    /* Delegate from document so links injected by previous AJAX still work */
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-scg-load-more]');
      if (!link) return;
      e.preventDefault();
      if (link.getAttribute('data-loading') === '1') return;

      var href = link.getAttribute('href');
      if (!href) return;

      /* Resolve section type for Shopify section rendering API */
      var gridSection = link.closest('[data-section-type]');
      var sectionType = gridSection
        ? gridSection.getAttribute('data-section-type')
        : 'sindhi-collection-grid';

      link.setAttribute('data-loading', '1');
      var originalLabel = link.textContent.trim();
      link.textContent = 'Loading…';

      /* Use sections API — returns JSON { "section-type": "<html>" } so we
         only parse the one section we need, not the full page. */
      var url = new URL(href, window.location.origin);
      url.searchParams.set('sections', sectionType);

      fetch(url.toString())
        .then(function (r) {
          if (!r.ok) throw new Error('Bad response: ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var sectionHtml = data[sectionType];
          if (!sectionHtml) throw new Error('Section missing from API response');

          var doc      = new DOMParser().parseFromString(sectionHtml, 'text/html');
          var destGrid = link.closest('section').querySelector('[data-scg-grid]');
          if (!destGrid) throw new Error('Destination grid not found');

          var srcGrid = doc.querySelector('[data-scg-grid]');
          if (!srcGrid) throw new Error('Source grid not found');

          srcGrid.querySelectorAll('.scg-card-wrap').forEach(function (card) {
            destGrid.appendChild(card);
          });

          var srcNext = doc.querySelector('[data-scg-load-more]');
          if (srcNext && srcNext.getAttribute('href')) {
            link.setAttribute('href', srcNext.getAttribute('href'));
            link.textContent = originalLabel;
            link.removeAttribute('data-loading');
          } else {
            var wrap = link.closest('.scg-pagination--load-more');
            if (wrap) wrap.style.display = 'none';
          }
        })
        .catch(function (err) {
          link.textContent = originalLabel;
          link.removeAttribute('data-loading');
          if (err && /Bad response/.test(err.message)) window.location.href = href;
        });
    });
  }

  /* ────────────────────────────────────────────────────────────────────────
     G. Infinite scroll (IntersectionObserver on sentinel)
     ──────────────────────────────────────────────────────────────────────── */
  function initInfiniteScroll() {
    var sentinels = qsa('[data-scg-sentinel]');
    if (!sentinels.length || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var sentinel = entry.target;
        var nextUrl  = sentinel.getAttribute('data-next-url');
        if (!nextUrl || sentinel.getAttribute('data-loading') === '1') return;

        sentinel.setAttribute('data-loading', '1');

        /* Use sections API — same as load-more, avoids full-page parse */
        var gridSection = sentinel.closest('[data-section-type]');
        var sectionType = gridSection
          ? gridSection.getAttribute('data-section-type')
          : 'sindhi-collection-grid';

        var url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('sections', sectionType);

        fetch(url.toString())
          .then(function (r) {
            if (!r.ok) throw new Error('Bad response: ' + r.status);
            return r.json();
          })
          .then(function (data) {
            var sectionHtml = data[sectionType];
            if (!sectionHtml) return;

            var doc      = new DOMParser().parseFromString(sectionHtml, 'text/html');
            var destGrid = sentinel.closest('section').querySelector('[data-scg-grid]');
            var srcGrid  = doc.querySelector('[data-scg-grid]');
            if (!destGrid || !srcGrid) return;

            srcGrid.querySelectorAll('.scg-card-wrap').forEach(function (card) {
              destGrid.appendChild(card);
            });

            var srcSentinel = doc.querySelector('[data-scg-sentinel]');
            var newNext     = srcSentinel ? srcSentinel.getAttribute('data-next-url') : '';
            if (newNext) {
              sentinel.setAttribute('data-next-url', newNext);
              sentinel.removeAttribute('data-loading');
            } else {
              io.unobserve(sentinel);
              sentinel.style.display = 'none';
            }
          })
          .catch(function (err) {
            sentinel.removeAttribute('data-loading');
            /* Fall back to hard navigation, same pattern as initLoadMore */
            if (err && /Bad response/.test(err.message)) window.location.href = nextUrl;
          });
      });
    }, { rootMargin: '400px 0px' });

    sentinels.forEach(function (s) { io.observe(s); });
  }

  /* ────────────────────────────────────────────────────────────────────────
     Bootstrap
     ──────────────────────────────────────────────────────────────────────── */
  function init() {
    /* No body-class guard — Liquid template gate in theme.liquid already
       restricts inclusion. Body-class check was preventing init on
       Horizon's template-collection (class may not be present in the
       form we expect). Verified bug 2026-05-21. */
    initFilterBarMorph();
    initSmartBannerTracking();
    initATC();
    initVariantPills();
    initWishlist();
    initLoadMore();
    initInfiniteScroll();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

}());
